import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { ADD_BACKEND_AUTH } from '@app/api/auth.tokens';

export interface PlanFeatures {
  mcp: boolean;
  gantt: boolean;
  slack: boolean;
  personal_plan: boolean;
  audit_log: boolean;
}

export interface BillingPlan {
  slug: string;
  name: string;
  price_monthly: string | null;
  purchasable: boolean;
  max_users: number | null;
  max_projects: number | null;
  features: PlanFeatures;
}

export interface SubscriptionInfo {
  status: string;
  plan: string;
  provider: string;
  seats: number | null;
  current_period_end: string | null;
  grace_period_until: string | null;
}

export interface SubscriptionStatus {
  company_id: number;
  company_name: string;
  plan: BillingPlan;
  /** The workspace's own plan (ignores the viewer's personal comp) — for the billing UI. */
  company_plan: BillingPlan;
  subscription: SubscriptionInfo | null;
  publishable_key: string | null;
  billable_seats: number;
}

export interface CheckoutResponse {
  url: string;
}

export interface CheckoutConfirmResult {
  status: 'active' | 'pending';
  company_id: number;
  plan: BillingPlan;
  company_plan: BillingPlan;
  subscription: SubscriptionInfo | null;
  billable_seats: number;
}

const authContext = new HttpContext().set(ADD_BACKEND_AUTH, true);

@Injectable({ providedIn: 'root' })
export class BillingService {
  private base = environment.backendDomain;

  constructor(private http: HttpClient) {}

  getPlans(): Observable<BillingPlan[]> {
    return this.http.get<BillingPlan[]>(
      `${this.base}/api/billing/plans/`,
      { context: authContext }
    );
  }

  getSubscription(companyId: number | string): Observable<SubscriptionStatus> {
    return this.http.get<SubscriptionStatus>(
      `${this.base}/api/billing/subscription/?company_id=${companyId}`,
      { context: authContext }
    );
  }

  createCheckout(companyId: number | string, plan: string): Observable<CheckoutResponse> {
    return this.http.post<CheckoutResponse>(
      `${this.base}/api/billing/checkout/`,
      { company_id: companyId, plan },
      { context: authContext }
    );
  }

  createPortal(companyId: number | string): Observable<CheckoutResponse> {
    return this.http.post<CheckoutResponse>(
      `${this.base}/api/billing/portal/`,
      { company_id: companyId },
      { context: authContext }
    );
  }

  confirmCheckout(sessionId: string): Observable<CheckoutConfirmResult> {
    return this.http.post<CheckoutConfirmResult>(
      `${this.base}/api/billing/checkout/confirm/`,
      { session_id: sessionId },
      { context: authContext }
    );
  }
}
